import Popover from "@mui/material/Popover";
import clsx from "clsx";
import React, { memo, useState } from "react";
import { useSelector } from "react-redux";
import ChatModalNew from "../../../../app/components/chat-modal-new/ChatModalNew";
import { COLORS, ROLES, ROUTES } from "../../../../core/consts/redesign/commonRedesign";
import { ArrowRight, Union } from "../../../../core/consts/redesign/icons";
import { CopyIcon } from "../../../../core/consts/redesign/icons/Copy";
import useIsMobile from "../../../../core/hooks/useIsMobile";
import { actions } from "../../../../core/store";
import { clientAgentInfoType } from "../../../../core/types/managerApiTypes";
import { rootState } from "../../../../core/types/rootState";
import { getColorByPyrusStatus, getColorByRole, getPyrusStatusText, getStatusDetails } from "../../../../core/utils/colorUtils";
import { copyToClipboard } from "../../../../core/utils/copyToClipboard";
import ButtonCustom from "../../../../UIcomponentsRedesign/ButtonCustom/ButtonCustom";
import IconButtonMain from "../../../../UIcomponentsRedesign/IconButtonMain/IconButtonMain";
import StatusCloud from "../../../../UIcomponentsRedesign/StatusCloud/StatusCloud";
import classes from "./UserCard.module.scss";

type PropsType = {
  clientInfo: clientAgentInfoType;
}

const UserCard: React.FC<PropsType> = ({
  clientInfo,
}) => {
  const initialUser = useSelector((state: rootState) => state.currentUser);
  const isManager = initialUser.role === ROLES.MANAGER;
  const isMaster = initialUser.role === ROLES.MASTER;
  const [copyInnPopoverAnchor, setCopyInnPopoverAnchor] = useState<HTMLElement | null>(null);
  const [copyAgentInnPopoverAnchor, setCopyAgentInnPopoverAnchor] = useState<HTMLElement | null>(null);
  const [copyPhonePopoverAnchor, setCopyPhonePopoverAnchor] = useState<HTMLElement | null>(null);
  const [currentChatId, setCurrentChatId] = useState<number | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const isMobile = useIsMobile();
  const clientName = clientInfo.companyName;
  const role = clientInfo.role;
  const clientStatus = clientInfo.status;
  const noShowMorePage = clientInfo.status === "Аккаунт не подтверждён";
  const region = clientInfo.region;
  const inn = clientInfo.inn;
  const phone = clientInfo.phone;
  const time = clientInfo.localTime;
  const registerDate = clientInfo.registerDate;
  const chatId = clientInfo?.chatIdWithClient;
  const manager = clientInfo?.manager;
  const agentInn = clientInfo.agent?.inn;
  const agentFio = clientInfo.agent?.fio;
  const pyrusStatus = clientInfo.synchronizePyrus;
  const hasPyrus = clientInfo.hasPyrus;
  const pyrusUrl = clientInfo.pyrusLink;
  const userId = clientInfo.userId;
  const clientUrl = isManager ? `${ROUTES.MANAGER}/client/${userId}` : `${ROUTES.ADMIN}/client/${userId}`;
  const { fontColor, bgColor, label } = getStatusDetails(clientStatus);

  const handleCopyPhone = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setCopyPhonePopoverAnchor(e.currentTarget);
    phone && copyToClipboard(phone.toString())
    setTimeout(() => {
      setCopyPhonePopoverAnchor(null);
    }, 2000);
  };

  const handleCopyInn = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setCopyInnPopoverAnchor(e.currentTarget);
    copyToClipboard(inn.toString());
    setTimeout(() => {
      setCopyInnPopoverAnchor(null);
    }, 2000);
  };

  const handleCopyAgentInn = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setCopyAgentInnPopoverAnchor(e.currentTarget);
    agentInn && copyToClipboard(agentInn.toString());
    setTimeout(() => {
      setCopyAgentInnPopoverAnchor(null);
    }, 2000);
  };

  const handleSelectChatType = () => {
    if (chatId) {
      setCurrentChatId(chatId);
      setIsChatOpen(true);
    } else {
      initiateChat(userId);
    }
  }

  const initiateChat = (userId: string) => {
    setIsChatOpen(true);
  }

  const closeChat = () => {
    setIsChatOpen(false)
    setCurrentChatId(null)
    actions.cleanCurrentChatData();
  }

  return (
    <>
      <div className={classes.cardContainer}>
        <div className={classes.card}>
          <div className={clsx(classes.colContainer, classes.nameWidth)}>
            {inn &&
              <div className={classes.copyBox}>
                <span className={classes.grayText}>ИНН {inn}</span>
                <IconButtonMain
                  title='Скопировать'
                  style={{ width: '16px', height: '16px' }}
                  handleClickEvent={(e) => handleCopyInn(e)}
                  color="noBgTransparent" icon={<CopyIcon />} />
                <Popover
                  open={Boolean(copyInnPopoverAnchor)}
                  anchorEl={copyInnPopoverAnchor}
                  onClose={() => setCopyInnPopoverAnchor(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}>
                  <div className={classes.copyConfirm}>
                    Данные скопированы
                  </div>
                </Popover>
              </div>}
            <span className={classes.blackText}>{clientName}</span>
            <div className={clsx(classes.copyBox, classes.statusWidth)}>
              <span className={classes.blackText}
                style={{
                  color: getColorByRole(role)
                }}>{role}</span>
              <StatusCloud
                status={label}
                statusBg={bgColor}
                statusTextColor={fontColor}
              />
            </div>
          </div>
          <div className={clsx(classes.colContainer, classes.regionWidth)}>
            {region &&
              <>
                <span className={classes.grayText}>Регион и время</span>
                <span className={classes.blackText}>{region}</span>
                <span className={classes.grayText}>{time}</span>
              </>
            }
          </div>
          <div className={clsx(classes.colContainer, classes.smallWidth)}>
            <span className={classes.grayText}>Регистрация</span>
            <span className={classes.blackText}>{registerDate}</span>
          </div>

          <div className={clsx(classes.colContainer, classes.smallWidth)}>
            {phone &&
              <>
                <div className={classes.copyBox}>
                  <span className={classes.grayText}>Телефон</span>
                  <IconButtonMain
                    title='Скопировать'
                    style={{ width: '16px', height: '16px' }}
                    handleClickEvent={(e) => handleCopyPhone(e)}
                    color="noBgTransparent" icon={<CopyIcon />} />
                  <Popover
                    open={Boolean(copyPhonePopoverAnchor)}
                    anchorEl={copyPhonePopoverAnchor}
                    onClose={() => setCopyPhonePopoverAnchor(null)}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}>
                    <div className={classes.copyConfirm}>
                      Данные скопированы
                    </div>
                  </Popover>
                </div>
                <span className={classes.blackText}>{phone}</span>
              </>
            }
          </div>
          {!isMaster &&
            <div className={clsx(classes.colContainerPyrus, classes.smallWidth)}>
              <div className={classes.pyrusBox}>
                {(hasPyrus && pyrusUrl) ?
                  <>
                    <a className={clsx(classes.copyBox, classes.noLinkStyle)}
                      title={pyrusUrl}
                      href={pyrusUrl}
                      target='_blank'
                      rel="noreferrer">
                      <span>Есть в Pyrus</span>
                      <Union />
                    </a>

                    {pyrusStatus !== null && (
                      <span
                        className={classes.blackText}
                        style={{
                          color: getColorByPyrusStatus(getPyrusStatusText(pyrusStatus)),
                        }}
                      >
                        {getPyrusStatusText(pyrusStatus)}
                      </span>
                    )}
                  </> :
                  <span className={classes.grayText}>Нет в Pyrus</span>}
              </div>
              {!noShowMorePage &&
                <a className={clsx(classes.copyBox, classes.goToPage)}
                  title={clientUrl}
                  href={clientUrl}
                  target='_blank'
                  rel="noreferrer">
                  <span>Данные</span>
                  <ArrowRight color={COLORS.brand_blue} />
                </a>}
            </div>}
        </div>

        <div className={classes.responsibleRow}>
          {!isManager && manager ?
            <>
              <div className={classes.resBox}>
                <span className={classes.grayText}>Менеджер</span>
                <span className={classes.blackText}>{manager}</span>
              </div>
              {!isMobile && manager && agentInn && <div className={classes.divider} />}
            </> : null}
          {agentInn ?
            <div className={classes.responsibleBox}>
              <div className={classes.innCreatorBox}>
                <span className={classes.grayTextStyled}>Агент ИНН {agentInn}</span>
                <IconButtonMain
                  title='Скопировать'
                  style={{ width: '16px', height: '16px' }}
                  handleClickEvent={(e) => handleCopyAgentInn(e)}
                  color="noBgTransparent" icon={<CopyIcon />} />
                <Popover
                  open={Boolean(copyAgentInnPopoverAnchor)}
                  anchorEl={copyAgentInnPopoverAnchor}
                  onClose={() => setCopyAgentInnPopoverAnchor(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}>
                  <div className={classes.copyConfirm}>
                    Данные скопированы
                  </div>
                </Popover>
              </div>
              <span className={classes.blackText}>{agentFio}</span>
            </div>
            : null}
          {isManager ?
            <ButtonCustom
              style={{
                padding: 0, margin: 0, minHeight: '21px'
              }}
              handleClick={() => handleSelectChatType()}
              rightIcon={<ArrowRight />}
              color='text_button'>
              Перейти в чат
            </ButtonCustom>
            : null
          }
        </div>
      </div>
      {isChatOpen &&
        (currentChatId ?
          <ChatModalNew
            open={isChatOpen}
            handleClose={closeChat}
            chatId={currentChatId}
            chatTitleChosen={`Чат с ${clientName}`}
          />
          :
          <ChatModalNew
            open={isChatOpen}
            handleClose={closeChat}
            chatByManager
            userId={userId}
            chatTitleChosen={`Чат с ${clientName}`}
          />

        )}
    </>
  )
}

export default memo(UserCard)
